// Bars renderer used by the "Technische Details" modal.
//
// Consumes the pre-derived `RankedSignalDto[]` directly
//   source = "global" → training-population aggregates (globalMeanAbsShap, globalMeanShap)
//   source = "local"  → this patient's aggregates       (localMeanAbsShap,  localMeanShap)
//   mode   = "importance"  → unsigned magnitude bar (single-direction, primary color)
//   mode   = "directional" → signed bar centered on zero (red = increases, green = decreases)

import { RankedSignalDto, RankedVariantDto } from "pokal_api"
import { featureIcon, featureLabel, variantBadge } from "./shapUtils"

type Source = "global" | "local"
type Mode = "importance" | "directional"

type Props = {
    signals: RankedSignalDto[]
    source: Source
    mode: Mode
}

// Extract the numeric value for a variant in the requested (source, mode).
// importance is unsigned magnitude; directional is the signed value.
function pickValue(v: RankedVariantDto, source: Source, mode: Mode): number {
    if (source === "global") {
        return mode === "importance" ? v.globalMeanAbsShap : v.globalMeanShap
    }
    return mode === "importance" ? v.localMeanAbsShap : v.localMeanShap
}

// Max value across all variants — the normalizer for bar width.  Always ≥ 1
// so a fully-empty signals[] doesn't divide-by-zero or produce 0-width bars
// for any variant that happens to have a small positive value.
function computeMaxValue(signals: RankedSignalDto[], source: Source, mode: Mode): number {
    let max = 1
    for (const s of signals) {
        for (const v of s.variants) {
            const value = mode === "importance" ? pickValue(v, source, mode) : Math.abs(pickValue(v, source, mode))
            if (value > max) max = value
        }
    }
    return max
}

// Geometry helpers — were previously imported from pokal_api but live here now.
// They're pure UI math (translate a numeric SHAP value to bar width / centered
// offset percentages) so they belong with the renderer that uses them.
function importanceBarWidth(value: number, maxValue: number): number {
    if (!Number.isFinite(value) || !Number.isFinite(maxValue) || maxValue <= 0) return 0
    return Math.max(0, Math.min(100, (value / maxValue) * 100))
}

function directionalBarGeometry(
    value: number,
    maxMagnitude: number,
): { leftPercent: number; widthPercent: number } {
    if (!Number.isFinite(value) || !Number.isFinite(maxMagnitude) || maxMagnitude <= 0) {
        return { leftPercent: 50, widthPercent: 0 }
    }
    const widthPercent = Math.max(
        0,
        Math.min(50, (Math.abs(value) / maxMagnitude) * 50),
    )
    const leftPercent = value >= 0 ? 50 : 50 - widthPercent
    return { leftPercent, widthPercent }
}

function directionalBarColor(value: number): string {
    if (value > 0) return "#dc2626" // red — increases prediction (risk)
    if (value < 0) return "#16a34a" // green — decreases prediction (protective)
    return "#9ca3af" // gray — exactly zero (rare)
}

function formatShapValue(value: number): string {
    if (!Number.isFinite(value)) return "–"
    return value.toFixed(4)
}

function DirectionalBar({ value, maxMagnitude }: { value: number; maxMagnitude: number }) {
    const bar = directionalBarGeometry(value, maxMagnitude)
    return (
        <div className={"relative h-3 overflow-hidden rounded-full bg-gray-200"}>
            <div className={"absolute inset-y-0 left-1/2 w-px bg-gray-400"} />
            <div
                className={"absolute inset-y-0 rounded-full"}
                style={{
                    left: `${bar.leftPercent}%`,
                    width: `${bar.widthPercent}%`,
                    backgroundColor: directionalBarColor(value),
                }}
            />
        </div>
    )
}

export default function ShapBarsSection({ signals, source, mode }: Props) {
    const maxValue = computeMaxValue(signals, source, mode)

    return (
        <div>
            {mode === "importance" ? (
                <>
                    <div className={"mb-1 text-sm font-semibold text-gray-700"}>
                        Einflussstärke aller Faktoren
                    </div>
                    <p className={"mb-3 text-xs text-gray-500"}>
                        Wie stark beeinflusst jeder Faktor die Vorhersage – unabhängig
                        davon, ob er das Risiko erhöht oder senkt?
                    </p>
                </>
            ) : (
                <>
                    <div className={"mb-1 text-sm font-semibold text-gray-700"}>
                        Richtungseffekt
                    </div>
                    <p className={"mb-3 text-xs text-gray-500"}>
                        Erhöht ein Faktor im Schnitt den vorhergesagten PHQ-8 (roter
                        Balken, rechts) oder senkt er ihn (grüner Balken, links)?
                    </p>
                    <div className={"mb-3 flex items-center gap-6 text-xs text-gray-600"}>
                        <div className={"flex items-center gap-1.5"}>
                            <span className={"h-2.5 w-2.5 rounded-sm bg-red-500"} />
                            <span>erhöht Risiko</span>
                        </div>
                        <div className={"flex items-center gap-1.5"}>
                            <span className={"h-2.5 w-2.5 rounded-sm bg-green-500"} />
                            <span>schützt vor Risiko</span>
                        </div>
                    </div>
                </>
            )}
            <div className={"space-y-3"}>
                {signals.map((signal) => {
                    const Icon = featureIcon(signal.groupKey)
                    return (
                        <div key={signal.groupKey}>
                            <div className={"mb-1 flex items-center gap-2"}>
                                <Icon className={"h-3.5 w-3.5 flex-shrink-0 text-gray-400"} />
                                <span className={"text-xs font-medium text-gray-600"}>
                                    {featureLabel(signal.groupKey)}
                                </span>
                            </div>
                            <div className={"space-y-1.5 pl-5"}>
                                {signal.variants.map((v) => {
                                    const badge = variantBadge(v.kind)
                                    const value = pickValue(v, source, mode)
                                    return (
                                        <div key={v.feature}>
                                            <div className={"mb-0.5 flex items-center justify-between gap-2"}>
                                                <div className={"flex items-center gap-1.5"}>
                                                    {badge && (
                                                        <span
                                                            className={
                                                                "rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
                                                            }
                                                        >
                                                            {badge}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className={"text-xs text-gray-400"}>
                                                    {formatShapValue(value)}
                                                </span>
                                            </div>
                                            {mode === "importance" ? (
                                                <div className={"h-3 overflow-hidden rounded-full bg-gray-200"}>
                                                    <div
                                                        className={"h-full rounded-full bg-primary-500"}
                                                        style={{ width: `${importanceBarWidth(value, maxValue)}%` }}
                                                    />
                                                </div>
                                            ) : (
                                                <DirectionalBar value={value} maxMagnitude={maxValue} />
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
